import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Stack,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import axios from '../../api/axios';
import MarkdownRenderer from '../Common/MarkdownRenderer';
import MarkdownEditor from '../Common/MarkdownEditor';

const IntroductionEditor = ({
  introduction,
  isTeacher = false,
  notebookId = null,
  sectionTitle = null,
  onUpdate = null,
}) => {
  const [editing, setEditing] = useState(false);
  const [editingContent, setEditingContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = () => {
    setEditingContent(introduction || '');
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditingContent('');
  };

  const handleSave = async () => {
    if (!notebookId || !sectionTitle) {
      return;
    }
    try {
      setSaving(true);
      await axios.put(`/notes/${notebookId}/section/${encodeURIComponent(sectionTitle)}`, {
        field: 'introduction',
        value: editingContent,
      });
      setEditing(false);
      setEditingContent('');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving introduction:', error);
      alert('保存失败：' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!notebookId || !sectionTitle) {
      return;
    }
    if (!window.confirm('确定要删除章节介绍吗？此操作不可撤销。')) {
      return;
    }
    try {
      setDeleting(true);
      await axios.put(`/notes/${notebookId}/section/${encodeURIComponent(sectionTitle)}`, {
        field: 'introduction',
        value: '',
      });
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting introduction:', error);
      alert('删除失败：' + (error.response?.data?.error || error.message));
    } finally {
      setDeleting(false);
    }
  };

  // 显示模式
  if (introduction && !editing) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}>
            章节介绍
          </Typography>
          {isTeacher && (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<EditIcon fontSize="small" />}
                onClick={handleEdit}
              >
                修改
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={handleDelete}
                disabled={deleting}
              >
                删除
              </Button>
            </Stack>
          )}
        </Box>
        <Box sx={{ color: 'text.primary' }}>
          <MarkdownRenderer content={introduction} />
        </Box>
      </Paper>
    );
  }

  // 添加按钮（当没有介绍时）
  if (!introduction && !editing && isTeacher) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: '2px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          textAlign: 'center',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleEdit}
          sx={{ textTransform: 'none' }}
        >
          添加章节介绍
        </Button>
      </Paper>
    );
  }

  // 编辑模式
  if (editing) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          backgroundColor: 'background.paper',
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1.1rem' }}>
            章节介绍
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="保存">
              <span>
                <IconButton
                  size="medium"
                  color="primary"
                  onClick={handleSave}
                  disabled={saving}
                  sx={{ p: 1.25 }}
                >
                  {saving ? <CircularProgress size={22} /> : <SaveIcon fontSize="medium" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="取消">
              <span>
                <IconButton
                  size="medium"
                  color="default"
                  onClick={handleCancel}
                  disabled={saving}
                  sx={{ p: 1.25 }}
                >
                  <CancelIcon fontSize="medium" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>
        <MarkdownEditor
          value={editingContent}
          onChange={setEditingContent}
          label="章节介绍"
          minRows={12}
          maxRows={20}
          showPreview={true}
          showToolbar={true}
          placeholder="输入Markdown格式的内容..."
          disabled={saving}
        />
      </Paper>
    );
  }

  return null;
};

export default IntroductionEditor;
